import { createSlice } from '@reduxjs/toolkit';

const basicDetailsSlice = createSlice({
    name: 'basicDetails',
    initialState: {
        formData: {
            // Basic personal info
            firstName: "",
            middleName: "",
            lastName: "",
            mobileNumber: "",
            emailAddress: "",
            fatherName: "",
            motherName: "",

            // Job-related fields
            entity: "",
            jobLocation: "",
            department: "",
            systemRole: "",
            designation: "",
            dateOfJoining: "",
            educationLevel: "",

            // Additional personal fields
            personalEmail: "",
            personalPhone: "",
            alternativePhone: "",
            gender: "",
            dob: "",
            nationality: "Indian",
            religion: "",
            bloodGroup: "",
            maritalStatus: "",
            spouseName: "",
            physicallyChallenged: "No",
            physicallyChallengedDetails: "",

            // Emergency and reference contacts
            emergencyRelation: "",
            emergencyName: "",
            emergencyPhone: "",
            refName: "",
            refPhone: "",
            refEmail: "",

            // Address fields
            presentAddress: {
                accommodationType: "",
                houseNo: "",
                floor: "",
                buildingName: "",
                area: "",
                landmark: "",
                city: "",
                state: "",
                pincode: "",
            },

            // Permanent address
            permanentAddress: {
                houseNo: "",
                floor: "",
                buildingName: "",
                area: "",
                landmark: "",
                city: "",
                state: "",
                pincode: "",
            },
            sameAsPresent: false,
            aadharNo: "",
            aadharName: "",
            panNo: "",
            panName: "",
            additionalIdType: "",
            voterId: "",
            drivingLicence: "",
            passportNo: "",
            passportName: "",
            passportExpiry: "",
            offerLetterAcceptance: [],
            appointmentLetterAcknowledgement: [],
            employeeId: null, // Add employeeId to track backend submission
            passportPhotoPath: "",
            nominees: [],
            disabilityCertificateFiles: [],
        },
        bankDetails: {
            accountName: "",
            accountNumber: "",
            bankName: "",
            ifscCode: "",
            branchName: "",
        },
        compensation: {
            ctc: "",
            gross: "",
            hasExistingPF: "",
            uanNumber: "",
            esicNumber: "",
            flexiBenefits: "",
            tds: "",
            needFillDetails: "",
            declaration: false,
        },
        joiningForms: {
            applicationFormITS: null,
            esicForm: null,
            form11: null,
            itsFormFGratuity: null,
            itsHRPOSHAcknowledgement: null,
            pfForm2: null,
            policeVerification: null,
        },
        stepCompletion: {
            basicDetails: false,
            personalInformation: false,
            bankDetails: false,
            education: false,
            previousEmployment: false,
            compensationBenefits: false,
            joiningForms: false,
        },
        isComplete: false,
        educationList: [
            {
                qualificationLevel: "",
                degree: "",
                yearOfJoining: "",
                yearOfCompletion: "",
                collegeName: "",
                university: "",
                cgpa: "",
                state: "",
                attachments: [],
            },
        ],
        employmentList: [
            {
                companyName: "",
                designation: "",
                ctc: "",
                joiningDate: "",
                relievingDate: "",
                address: "",
                hrName: "",
                hrPhone: "",
                hrEmail: "",
                managerName: "",
                managerPhone: "",
                references: [
                    {
                        name: "",
                        phone: "",
                        email: "",
                    },
                ],
                offerLetterFiles: [],
                incrementLetterFiles: [],
                payslipFiles: [],
                experienceLetterFiles: [],
                relievingLetterFiles: [],
                isLatest: false,
            },
        ],
    },
    reducers: {
        setBasicDetails: (state, action) => {
            // Optimized: Only update if payload has changes
            if (action.payload && typeof action.payload === 'object') {
                Object.assign(state.formData, action.payload);
            }
        },
        updateAddressField: (state, action) => {
            const { addressType, field, value } = action.payload;
            if (!addressType || !field) return; // Validation

            // Initialize address object if it doesn't exist
            if (!state.formData[addressType]) {
                state.formData[addressType] = {};
            }

            // Only update if value has actually changed
            if (state.formData[addressType][field] !== value) {
                state.formData[addressType][field] = value;
            }
        },
        setBasicDetailsComplete: (state, action) => {
            state.isComplete = action.payload;
            state.stepCompletion.basicDetails = action.payload;
        },
        setStepCompletion: (state, action) => {
            const { step, isCompleted } = action.payload;
            if (state.stepCompletion.hasOwnProperty(step)) {
                state.stepCompletion[step] = isCompleted;
            }
        },
        resetBasicDetails: (state) => {
            state.formData = {
                // Basic personal info
                firstName: "",
                middleName: "",
                lastName: "",
                mobileNumber: "",
                emailAddress: "",

                // Job-related fields
                entity: "",
                jobLocation: "",
                department: "",
                systemRole: "",
                designation: "",
                dateOfJoining: "",
                educationLevel: "",

                // Additional personal fields
                gender: "",
                dob: "",
                bloodGroup: "",
                personalEmail: "",
                personalPhone: "",
                alternativePhone: "",
                maritalStatus: "",
                spouseName: "",
                physicallyChallenged: "No",
                physicallyChallengedDetails: "",
                fatherName: "",
                motherName: "",
                nationality: "Indian",
                religion: "",
                emergencyRelation: "",
                emergencyName: "",
                emergencyPhone: "",
                aadharNo: "",
                aadharName: "",
                panNo: "",
                panName: "",
                offerLetterAcceptance: [],
                appointmentLetterAcknowledgement: [],
                passportPhotoPath: "",
            };
            state.isComplete = false;
            state.educationList = [
                {
                    qualificationLevel: "",
                    degree: "",
                    yearOfJoining: "",
                    yearOfCompletion: "",
                    collegeName: "",
                    university: "",
                    cgpa: "",
                    state: "",
                    attachments: [],
                },
            ];
        },
        setEducationList: (state, action) => {
            state.educationList = action.payload;
        },
        setEmploymentList: (state, action) => {
            state.employmentList = action.payload;
        },
        updateEmploymentItem: (state, action) => {
            const { index, field, value } = action.payload;
            if (typeof index !== 'number' || index < 0 || !field) return; // Validation

            const item = state.employmentList[index];
            if (item && item[field] !== value) {
                item[field] = value;
            }
        },
        updateEducationItem: (state, action) => {
            const { index, field, value } = action.payload;
            if (typeof index !== 'number' || index < 0 || !field) return; // Validation

            const item = state.educationList[index];
            if (item && item[field] !== value) {
                item[field] = value;
            }
        },
        setBankDetails: (state, action) => {
            if (action.payload && typeof action.payload === 'object') {
                Object.assign(state.bankDetails, action.payload);
            }
        },
        setCompensation: (state, action) => {
            if (action.payload && typeof action.payload === 'object') {
                Object.assign(state.compensation, action.payload);
            }
        },
        setJoiningForms: (state, action) => {
            if (action.payload && typeof action.payload === 'object') {
                Object.assign(state.joiningForms, action.payload);
            }
        },
    },
});

export const { setBasicDetails, setBasicDetailsComplete, setStepCompletion, resetBasicDetails, setEducationList, setEmploymentList, updateEmploymentItem, updateEducationItem, updateAddressField, setBankDetails, setCompensation, setJoiningForms } = basicDetailsSlice.actions;
export default basicDetailsSlice.reducer;
